import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
// import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "../lib/queryClient";
import { nanoid } from "nanoid";
import { useToast } from "./use-toast";

export function useFormBuilder(initialForm) {
  const [form, setForm] = useState(initialForm);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // const createFormMutation = useMutation({
  //   mutationFn: async (formData) => {
  //    //console.log("Creating form with data:", formData);

  //     const response = await apiRequest("POST", `${process.env.REACT_APP_API_URL}/api/admin/dynamicform/saveforms`, formData);
  //    //console.log("Create form response:", response);
  //     return response.json();
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: ["/admin/dynamicform/saveforms"] });
  //     queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
  //     toast({
  //       title: "Form created",
  //       description: "Your form has been saved successfully.",
  //     });
  //   },
  //   onError: (error) => {
  //     toast({
  //       title: "Error",
  //       description: "Failed to save form. Please try again.",
  //       variant: "destructive",
  //     });
  //     console.error("Failed to create form:", error);
  //   },
  // });
  const createFormMutation = useMutation({
    mutationFn: async (formData) => {
     //console.log("Creating form with data:", formData);

      const response = await apiRequest(
        "POST",
        `${process.env.REACT_APP_API_URL}/api/admin/dynamicform/saveforms`,
        formData
      );

      if (!response.ok) {
        throw new Error("Failed to save form");
      }

      const data = await response.json();
     //console.log("Create form response:", data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-dynamicform-saveforms"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast({
        title: "Form created",
        description: "Your form has been saved successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save form. Please try again.",
        variant: "destructive",
      });
      console.error("Failed to create form:", error);
    },
  });

  const updateFormMutation = useMutation({
    mutationFn: async (formData) => {
      if (!form._id) throw new Error("Form ID is required for updates");
      const response = await apiRequest("PUT", `/api/forms/${form._id}`, formData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forms"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      toast({
        title: "Form updated",
        description: "Your changes have been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update form. Please try again.",
        variant: "destructive",
      });
      console.error("Failed to update form:", error);
    },
  });

  const updateForm = useCallback((updates) => {
    setForm(prev => ({ ...prev, ...updates }));
  }, []);

  const addField = useCallback((fieldType) => {
    const newField = {
      id: nanoid(),
      type: fieldType,
      label: `${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} Field`,
      placeholder: "",
      required: false,
      unique: false,
      width: "full",
      options: fieldType === "select" || fieldType === "radio" ? ["Option 1", "Option 2"] : undefined,
      validation: {},
      order: form.fields ? form.fields.length : 0,
    };

    setForm(prev => ({
      ...prev,
      fields: [...(prev.fields || []), newField],
    }));
  }, [form.fields]);

  const updateField = useCallback((fieldId, updates) => {
    setForm(prev => ({
      ...prev,
      fields: (prev.fields || []).map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      ),
    }));
  }, []);

  const deleteField = useCallback((fieldId) => {
    setForm(prev => ({
      ...prev,
      fields: (prev.fields || []).filter(field => field.id !== fieldId),
    }));
  }, []);

  const saveForm = useCallback(async () => {
    if (form._id) {
      // Update existing form
      await updateFormMutation.mutateAsync(form);
    } else {
      // Create new form
      const result = await createFormMutation.mutateAsync(form);
      setForm(prev => ({ ...prev, _id: result._id }));
    }
  }, [form, createFormMutation, updateFormMutation]);

  return {
    form,
    updateForm,
    addField,
    updateField,
    deleteField,
    saveForm,
    isSaving: createFormMutation.isPending || updateFormMutation.isPending,
  };
}